import {
  AppBar,
  Button,
  Grid,
  InputAdornment,
  InputBase,
  inputBaseClasses,
  Paper,
  SelectChangeEvent,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Toolbar,
  Typography,
} from '@mui/material';
import usePagination from '@mui/material/usePagination/usePagination';
import { Magnifier } from 'asset';
import {
  DeleteDialog,
  HeadTable,
  Layout,
  PopupCreateEditPackage,
  TableCaption,
} from 'components';
import { ButtonDelete, ButtonEdit } from 'components/common/table';
import { drawerWidth } from 'config';
import { getSortItem } from 'helpers';
import { useDateTime } from 'hooks';
import useDebounce from 'hooks/useDebounce';
import { TYPE_POPUP } from 'models';
import {
  PACKAGE_LIST_PARAMS_REQUEST,
  PACKAGE_STATUS,
  PACKAGE_TYPE,
} from 'models/package';
import React, { useEffect, useMemo, useState } from 'react';
import { useAppDispatch, useAppSelector } from 'store/hook';
import { setPackageListParams } from 'store/reducers/package';
import { getPackageListRequest } from 'store/reducers/package/actionType';
import { headCells } from './data';

const PackagesAC = () => {
  const dispatch = useAppDispatch();
  const { formatDateTime } = useDateTime();
  const { package_list } = useAppSelector(
    (state) => state.packageReducer.package
  );
  const { params } = package_list;

  const [keyword, setKeyword] = useState('');
  const [openCreatePopup, setOpenCreatePopup] = useState(false);
  const [openEditPopup, setOpenEditPopup] = useState(false);
  const [openDeletePopup, setOpenDeletePopup] = useState(false);
  const [itemPackage, setItemPackage] = useState<PACKAGE_TYPE>();

  const onSetListParams = (params: PACKAGE_LIST_PARAMS_REQUEST | null) => {
    dispatch(setPackageListParams(params));
  };

  useEffect(() => {
    if (params) {
      dispatch(getPackageListRequest(params));
    }
  }, [params, dispatch]);

  useEffect(() => {
    setKeyword(params?.search ?? '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setOpenCreate = () => {
    setOpenCreatePopup(true);
  };

  const setOpenEdit = (event: any, data: PACKAGE_TYPE) => {
    event.stopPropagation();
    setItemPackage(data);
    setOpenEditPopup(true);
  };

  const setOpenDelete = (event: any, data: PACKAGE_TYPE) => {
    event.stopPropagation();
    setItemPackage(data);
    setOpenDeletePopup(true);
  };

  const handleChangeRowsPerPage = (event: SelectChangeEvent) => {
    const limit = parseInt(event.target.value, 10);
    onSetListParams({ ...params, page: 1, limit });
  };

  const { items } = usePagination({
    count: package_list.metadata.total_pages,
    page: package_list.metadata.page,
    onChange(_event, page) {
      onSetListParams({ ...params, page: page });
    },
  });

  const onSetKeywordParams = useDebounce((keyword: string) => {
    const _params = { ...params, page: 1 };
    if (keyword) _params.search = keyword;
    else delete _params.search;
    onSetListParams(_params);
  }, 250);

  const onChangeSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    setKeyword(event.target.value);
    onSetKeywordParams(event.target.value);
  };

  const onClosePopup = (type: number) => {
    switch (type) {
      case TYPE_POPUP.DELETE:
        setOpenDeletePopup(false);
        setItemPackage(undefined);
        break;
      case TYPE_POPUP.EDIT:
        setOpenEditPopup(false);
        setItemPackage(undefined);
        break;
      case TYPE_POPUP.CREATE_OR_EDIT:
        setOpenCreatePopup(false);
        setItemPackage(undefined);
        break;
    }
  };

  const onReloadList = () => {
    if (!params) return;
    dispatch(getPackageListRequest(params));
  };

  const sortItem = useMemo(() => {
    return getSortItem(package_list.params?.sort);
  }, [package_list.params]);

  const handleRequestSort = (
    _: React.MouseEvent<unknown>,
    property: keyof PACKAGE_TYPE
  ) => {
    const isAsc = sortItem?.orderBy === property && sortItem?.order === 'asc';
    onSetListParams({
      ...params,
      sort: `${property}-${isAsc ? 'desc' : 'asc'}`,
    });
  };

  return (
    <Layout>
      <AppBar
        position="fixed"
        sx={{
          width: `calc(100% - ${drawerWidth}px)`,
          ml: `${drawerWidth}px`,
          backgroundColor: 'white.light',
          borderBottom: '1px solid rgba(0, 0, 0, 0.08)',
        }}
        elevation={0}
      >
        <Toolbar
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
          }}
        >
          <Grid>
            <Typography
              sx={{
                fontSize: '20px',
                lineHeight: 1,
                color: 'grey1.light',
              }}
            >
              Concierge / Packages
            </Typography>
          </Grid>
          <Grid display="flex" gap="8px" flexGrow={1} justifyContent="center">
            <InputBase
              placeholder="Search"
              sx={{
                maxWidth: '369px',
                height: '41px',
                flexGrow: 1,
                background: '#F2F4F6',
                border: '1px solid #D9D9D9',
                p: '0px 7px 0px 11px',
                borderRadius: '5px',
                [`& .${inputBaseClasses.input}`]: {
                  fontSize: 14,
                  fontWeight: 400,
                  lineHeight: '100%',
                  p: 0,
                  height: 'fit-content',
                },
              }}
              onChange={onChangeSearch}
              value={keyword}
              endAdornment={
                <InputAdornment position="end">
                  <Magnifier />
                </InputAdornment>
              }
            />
          </Grid>
          <Stack spacing={1} direction="row">
            <Button
              variant="contained"
              color="secondary"
              sx={{
                height: 30,
                width: '72px',
                fontWeight: 600,
                fontSize: '13px',
                lineHeight: '100%',
              }}
              onClick={setOpenCreate}
            >
              Create
            </Button>
          </Stack>
        </Toolbar>
      </AppBar>
      <Stack spacing="8px">
        <TableContainer
          component={Paper}
          elevation={0}
          sx={{
            border: '1px solid #D9D9D9',
            maxHeight: 'calc(100vh - 150px)',
          }}
        >
          <Table
            stickyHeader
            sx={{ minWidth: 650 }}
            aria-labelledby="tableTitle"
          >
            <HeadTable<PACKAGE_TYPE>
              order={sortItem?.order}
              orderBy={sortItem?.orderBy}
              rowCount={package_list.records.length}
              headCells={headCells}
              onCheckbox={false}
              onRequestSort={handleRequestSort}
            />
            <TableBody
              sx={{
                height: 'auto !important',
              }}
            >
              {package_list.records.map((row, index) => {
                return (
                  <TableRow
                    hover
                    role="checkbox"
                    tabIndex={-1}
                    key={row.id ?? index}
                  >
                    <TableCell
                      component="th"
                      scope="row"
                      align="left"
                      sx={{
                        borderRight: '1px solid #DEE2E6',
                        padding: '18px 12px 17px',
                        fontSize: '14px',
                        fontWeight: 400,
                      }}
                    >
                      {row?.name}
                    </TableCell>

                    <TableCell
                      component="th"
                      scope="row"
                      align="left"
                      sx={{
                        borderRight: '1px solid #DEE2E6',
                        padding: '18px 12px 17px',
                        fontSize: '14px',
                        fontWeight: 400,
                      }}
                    >
                      {row?.description}
                    </TableCell>

                    <TableCell
                      align="left"
                      sx={{
                        borderRight: '1px solid #DEE2E6',
                        padding: '18px 12px 17px',
                        fontSize: '14px',
                        fontWeight: 400,
                      }}
                    >
                      ${row?.parent_rate}
                    </TableCell>

                    <TableCell
                      align="left"
                      sx={{
                        borderRight: '1px solid #DEE2E6',
                        padding: '18px 12px 17px',
                        fontSize: '14px',
                        fontWeight: 400,
                      }}
                    >
                      ${row?.sitter_rate}
                    </TableCell>

                    <TableCell
                      align="left"
                      sx={{
                        borderRight: '1px solid #DEE2E6',
                        padding: '18px 12px 17px',
                        fontSize: '14px',
                        fontWeight: 400,
                      }}
                    >
                      ${row?.price}
                    </TableCell>

                    <TableCell
                      align="left"
                      sx={{
                        borderRight: '1px solid #DEE2E6',
                        padding: '18px 12px 17px',
                        fontSize: '14px',
                        fontWeight: 400,
                      }}
                    >
                      {row?.tag}
                    </TableCell>

                    <TableCell
                      align="left"
                      sx={{
                        borderRight: '1px solid #DEE2E6',
                        padding: '18px 12px 17px',
                        fontSize: '14px',
                        fontWeight: 400,
                      }}
                    >
                      <Grid
                        container
                        alignItems="center"
                        textTransform="capitalize"
                      >
                        <Grid
                          sx={{
                            width: '8px',
                            height: '8px',
                            borderRadius: '50%',
                            marginRight: '5px',
                            backgroundColor:
                              row.status === PACKAGE_STATUS.ACTIVE
                                ? 'green.light'
                                : row.status === PACKAGE_STATUS.INACTIVE
                                ? 'red.light'
                                : '',
                          }}
                        />
                        <Typography>{row?.status}</Typography>
                      </Grid>
                    </TableCell>

                    <TableCell
                      align="left"
                      sx={{
                        borderRight: '1px solid #DEE2E6',
                        padding: '18px 12px 17px',
                        fontSize: '14px',
                        fontWeight: 400,
                      }}
                    >
                      {formatDateTime(row?.createdAt)}
                    </TableCell>

                    <TableCell
                      align="left"
                      sx={{
                        borderRight: '1px solid #DEE2E6',
                        padding: '18px 12px 17px',
                      }}
                    >
                      <Stack direction="row" spacing="10px">
                        <ButtonEdit onClick={(e) => setOpenEdit(e, row)} />
                        <ButtonDelete onClick={(e) => setOpenDelete(e, row)} />
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
        <TableCaption
          rowsPerPage={params?.limit ?? 0}
          handleChangeRowsPerPage={handleChangeRowsPerPage}
          metadata={package_list.metadata}
          items={items}
        />
      </Stack>
      {openDeletePopup && (
        <DeleteDialog
          name={itemPackage?.id}
          open={openDeletePopup}
          onClose={() => onClosePopup(TYPE_POPUP.DELETE)}
          subject={itemPackage?.id}
          onSuccess={onReloadList}
          type="package"
        />
      )}
      {openCreatePopup && (
        <PopupCreateEditPackage
          open={true}
          onSuccess={onReloadList}
          handleClosePopup={() => onClosePopup(TYPE_POPUP.CREATE_OR_EDIT)}
        />
      )}
      {openEditPopup && (
        <PopupCreateEditPackage
          open
          subject={itemPackage}
          onSuccess={onReloadList}
          handleClosePopup={() => onClosePopup(TYPE_POPUP.EDIT)}
        />
      )}
    </Layout>
  );
};

export default PackagesAC;
